// What the partner page shows, and how each action moves it on. Kept apart from React so
// the signed-in behaviour is tested with a stubbed Neon Auth client and a stubbed
// /api/partner/me (flow.test.ts); PartnersPage.tsx only renders the screen it is given.
import type { PartnerMe, PartnerRecord, ProfileChange, Referral } from './api';
import { PartnerApiError } from './api';
import { isEmailNotVerified, type PartnerAuth } from './auth';
import { isProfilePath, isReferPath, leadIdOf } from './leads';
import { type ReferralBody, referralBody, type ReferralErrors, type ReferralValues, validateReferral } from './referral';
import { type FieldErrors, registrationBody, type RegistrationValues, validateRegistration } from './registration';

/**
 * The Apply page after a submit that did not make a record: what was typed (so it stays),
 * each field's message, a message above the form, and whether the send itself failed
 * (`retry`: the button reads Try Again).
 */
export interface ApplicationState {
  values: RegistrationValues;
  fieldErrors?: FieldErrors;
  error?: string;
  retry?: boolean;
}

/**
 * The Refer a Company form after a submit that did not make a lead (MOTHER-88): what was
 * typed (so it stays), each field's message (`consent` for the checkbox), a message above
 * the form, and whether the send itself failed (`retry`: the button reads Try Again).
 */
export interface ReferralFormState {
  values: ReferralValues;
  fieldErrors?: ReferralErrors;
  error?: string;
  retry?: boolean;
}

/** My Profile's two editable fields as typed (MOTHER-182). */
export interface ProfileValues {
  phone: string;
  companyWebsite: string;
}
export type ProfileKey = keyof ProfileValues;
const PROFILE_KEYS: ProfileKey[] = ['phone', 'companyWebsite'];

/**
 * My Profile after a save that changed nothing: what was typed (so it stays), each
 * field's message, the server's message above the form, and whether the send itself
 * failed (`retry`: Save reads Try Again).
 */
export interface ProfileFormState {
  values: ProfileValues;
  fieldErrors?: Partial<Record<ProfileKey, string>>;
  error?: string;
  retry?: boolean;
}

/** The two fields as /api/partner/me last answered them; a missing one is empty. */
export function profileValuesOf(me: PartnerMe): ProfileValues {
  return { phone: me.partner?.phone ?? '', companyWebsite: me.partner?.companyWebsite ?? '' };
}

/**
 * The PATCH body: only the fields whose trimmed value differs from what was loaded,
 * trimmed, with a cleared field null. Empty when nothing changed, so Save stays disabled.
 */
export function profileChange(loaded: ProfileValues, values: ProfileValues): ProfileChange {
  const change: ProfileChange = {};
  for (const key of PROFILE_KEYS) {
    const typed = values[key].trim();
    if (typed !== loaded[key].trim()) change[key] = typed === '' ? null : typed;
  }
  return change;
}

/**
 * A verified partner's page (MOTHER-87): My Leads with their leads, one lead (`notice`
 * says it was just submitted, MOTHER-88), a lead id the API does not know (another
 * partner's or made-up), a failed read (`failed`, worth a Try Again on `path`), the
 * Refer a Company form (`form` after a refused submit, MOTHER-88), or My Profile
 * (MOTHER-182: `form` after a failed save, `notice` after a saved one).
 */
export type LeadsView =
  | { kind: 'list'; referrals: Referral[] }
  | { kind: 'lead'; referral: Referral; notice?: string }
  | { kind: 'no-such-lead' }
  | { kind: 'failed'; path: string }
  | { kind: 'refer'; form?: ReferralFormState }
  | { kind: 'profile'; form?: ProfileFormState; notice?: string };

export type Screen =
  | { kind: 'loading' }
  | { kind: 'sign-in'; notice?: string; error?: string }
  | { kind: 'sign-up'; error?: string }
  /** The six-digit code step for `email`; `notice` says a new code was sent. */
  | { kind: 'verify'; email: string; notice?: string; error?: string }
  /**
   * Signed in: PartnersPage.tsx picks the page from `me.partner` (Apply, pending, declined,
   * deactivated, or the verified partner's). `application` is the Apply page after a refused
   * submit. `leads` is a verified partner's page once read (`openLeads`); until then it is
   * loading. `error` says a Sign Out failed: the session is still live, so the page stays
   * here and Sign Out retries.
   */
  | { kind: 'signed-in'; me: PartnerMe; application?: ApplicationState; leads?: LeadsView; error?: string }
  /** /api/partner/me or Neon Auth could not answer: worth a retry. */
  | { kind: 'error'; message: string };

export interface FlowDeps {
  auth: PartnerAuth;
  fetchMe(): Promise<PartnerMe>;
  /** POST /api/partner/registration with the seven fields: the new pending record. */
  register(body: RegistrationValues): Promise<PartnerRecord>;
  /** GET /api/partner/referrals: a verified partner's own leads. */
  fetchReferrals(): Promise<Referral[]>;
  /** GET /api/partner/referrals/:id: one of them. */
  fetchReferral(id: string): Promise<Referral>;
  /** POST /api/partner/referrals with the ten fields and `consent: true`: the new lead. */
  refer(body: ReferralBody): Promise<Referral>;
  /** PATCH /api/partner/me with only the changed fields: who they now are. */
  updateProfile(change: ProfileChange): Promise<PartnerMe>;
}

export const SIGN_IN_PATH = '/sign-in';
export const SIGN_UP_PATH = '/sign-up';

const CHECK_FAILED = 'We could not reach the partner portal. Please try again.';
const CONFIRMED = 'Your email is confirmed. Sign in to continue.';
export const SIGN_OUT_FAILED = 'We could not sign you out. Please try again.';
export const CODE_SENT = 'New code sent. Check your email.';
export const SEND_FAILED = 'We could not send your application. Please try again.';
export const REFER_FAILED = 'We could not send your lead. Please try again.';
export const LEAD_SUBMITTED = 'Lead submitted';
export const PROFILE_SAVED = 'Saved.';
export const SAVE_FAILED = 'We could not save your changes. Please try again.';

/** The signed-out page for a path: /sign-up is the sign-up form; anything else is the sign-in form. */
export function signedOutScreen(pathname: string): Screen {
  return pathname === SIGN_UP_PATH ? { kind: 'sign-up' } : { kind: 'sign-in' };
}

/** `p***@example.test`: enough to recognise the address, not to read it over a shoulder. */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at <= 0) return email;
  return `${email[0]}***${email.slice(at)}`;
}

function message(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0 ? error.message : fallback;
}

export interface PartnerFlow {
  /** On load, and on Retry: a live session goes to /api/partner/me; none shows the path's form. */
  load(pathname: string): Promise<Screen>;
  signIn(input: { email: string; password: string }): Promise<Screen>;
  signUp(input: { firstName: string; lastName: string; email: string; password: string }): Promise<Screen>;
  verify(email: string, code: string): Promise<Screen>;
  resend(email: string): Promise<Screen>;
  /** Resolves only on failure: on success the browser has gone to Google. */
  google(from: Screen): Promise<Screen>;
  /**
   * Ends the session and shows the sign-in form. If Neon Auth refuses or cannot be reached,
   * the session is still live: stays on the holder screen for `me` with an error, never
   * claiming the person is signed out.
   */
  signOut(me: PartnerMe): Promise<Screen>;
  /**
   * Submits the application for `me`, who has no partner record. Checked here first, by
   * the API's rules: a failing field sends nothing. A 201 is the pending page; a refusal
   * keeps the typed values and says why; a 401 is the sign-in form.
   */
  apply(me: PartnerMe, values: RegistrationValues): Promise<Screen>;
  /**
   * A verified partner's page for `pathname`: `/refer` is the Refer a Company form and
   * `/profile` My Profile (both read from nothing: My Profile shows `me`, which
   * /api/partner/me answered), `/leads/<id>` is that lead (a 404 is "No such lead."), anything else
   * My Leads. A 403 (deactivated meanwhile) re-reads
   * /api/partner/me and shows that state's page; a 401 is the sign-in form; anything else
   * is `failed`, with Try Again.
   */
  openLeads(me: PartnerMe, pathname: string): Promise<Screen>;
  /**
   * Refers a company for verified `me` (MOTHER-88). Checked here first, by the API's
   * rules: a failing field sends nothing. A 201 is the new lead's page, saying it was
   * submitted; a 400 keeps the typed values and puts each message beside its field; a 403
   * re-reads /api/partner/me and shows that state's page; a 401 is the sign-in form;
   * anything else keeps the typed values, with Try Again.
   */
  refer(me: PartnerMe, values: ReferralValues): Promise<Screen>;
  /**
   * Saves verified `me`'s phone and website (MOTHER-182): one PATCH with only the fields
   * that changed from `me`; nothing changed sends nothing. A 200 is My Profile with the
   * answer's values and "Saved."; a 400 keeps the typed values, with the server's message
   * and each field's; a 403 re-reads /api/partner/me and shows that state's page; a 401 is
   * the sign-in form; anything else keeps the typed values, with Try Again.
   */
  saveProfile(me: PartnerMe, values: ProfileValues): Promise<Screen>;
}

export function createPartnerFlow({ auth, fetchMe, register, fetchReferrals, fetchReferral, refer, updateProfile }: FlowDeps): PartnerFlow {
  /**
   * The password typed at sign-up (or at a sign-in refused for an unverified address),
   * held in memory only, so the code step can sign the person straight in. Never
   * stored; after a reload they sign in again.
   */
  let held: { email: string; password: string } | null = null;

  function takePassword(email: string): string | null {
    const password = held !== null && held.email.toLowerCase() === email.toLowerCase() ? held.password : null;
    held = null;
    return password;
  }

  /**
   * Cleanup after the server refused the session (a 401): best effort, because the page
   * shows the sign-in form either way and /api/partner/* already turns the session away.
   * An explicit Sign Out does not use this; it reports a failure (`signOut` below).
   */
  async function dropRefusedSession(): Promise<void> {
    try {
      await auth.signOut();
    } catch {
      // Best effort, as above.
    }
  }

  /** Signed in: the server decides. A 401 drops the session; anything else is retryable. */
  async function afterSignIn(): Promise<Screen> {
    try {
      return { kind: 'signed-in', me: await fetchMe() };
    } catch (error) {
      if (error instanceof PartnerApiError && error.status === 401) {
        await dropRefusedSession();
        return { kind: 'sign-in', notice: error.message };
      }
      return { kind: 'error', message: CHECK_FAILED };
    }
  }

  /**
   * A verified partner's request got a 403: no longer verified. Whatever /api/partner/me
   * now says picks the page. If it still says verified, `stillVerified` shows the failure
   * rather than asking again forever.
   */
  async function afterForbidden(stillVerified: (me: PartnerMe) => Screen): Promise<Screen> {
    const next = await afterSignIn();
    return next.kind === 'signed-in' && next.me.partner?.status === 'verified' ? stillVerified(next.me) : next;
  }

  return {
    async load(pathname) {
      let token: string | null;
      try {
        token = await auth.getToken();
      } catch {
        return { kind: 'error', message: CHECK_FAILED };
      }
      return token === null ? signedOutScreen(pathname) : afterSignIn();
    },

    async signIn({ email, password }) {
      const address = email.trim();
      try {
        await auth.signInWithEmail(address, password);
      } catch (error) {
        if (isEmailNotVerified(error)) {
          held = { email: address, password };
          return { kind: 'verify', email: address };
        }
        return { kind: 'sign-in', error: message(error, 'Unable to sign in.') };
      }
      return afterSignIn();
    },

    async signUp({ firstName, lastName, email, password }) {
      const address = email.trim();
      try {
        await auth.signUpWithEmail({ name: `${firstName.trim()} ${lastName.trim()}`, email: address, password });
      } catch (error) {
        return { kind: 'sign-up', error: message(error, 'Unable to create your account.') };
      }
      held = { email: address, password };
      return { kind: 'verify', email: address };
    },

    async verify(email, code) {
      try {
        await auth.verifyEmail(email, code);
      } catch (error) {
        return { kind: 'verify', email, error: message(error, 'That code did not work.') };
      }
      try {
        // Neon Auth may sign the person in on verification; if not, use the password from this visit.
        if ((await auth.getToken()) === null) {
          const password = takePassword(email);
          if (password !== null) await auth.signInWithEmail(email, password);
        }
        if ((await auth.getToken()) === null) return { kind: 'sign-in', notice: CONFIRMED };
      } catch {
        return { kind: 'sign-in', notice: CONFIRMED };
      }
      return afterSignIn();
    },

    async resend(email) {
      try {
        await auth.sendCode(email);
      } catch (error) {
        return { kind: 'verify', email, error: message(error, 'Unable to send a new code.') };
      }
      return { kind: 'verify', email, notice: CODE_SENT };
    },

    async google(from) {
      try {
        await auth.signInWithGoogle();
        return from;
      } catch (error) {
        const text = message(error, 'Unable to start Google sign-in.');
        return from.kind === 'sign-up' ? { kind: 'sign-up', error: text } : { kind: 'sign-in', error: text };
      }
    },

    async signOut(me) {
      held = null;
      try {
        await auth.signOut();
      } catch {
        return { kind: 'signed-in', me, error: SIGN_OUT_FAILED };
      }
      return { kind: 'sign-in' };
    },

    async apply(me, values) {
      const fieldErrors = validateRegistration(values);
      if (Object.keys(fieldErrors).length > 0) return { kind: 'signed-in', me, application: { values, fieldErrors } };
      let partner: PartnerRecord;
      try {
        partner = await register(registrationBody(values));
      } catch (error) {
        if (!(error instanceof PartnerApiError)) return { kind: 'signed-in', me, application: { values, error: SEND_FAILED, retry: true } };
        if (error.status === 401) {
          await dropRefusedSession();
          return { kind: 'sign-in', notice: error.message };
        }
        if (error.status === 400 && Object.keys(error.fields).length > 0) {
          return { kind: 'signed-in', me, application: { values, fieldErrors: error.fields } };
        }
        if (error.status === 400 || error.status === 409) return { kind: 'signed-in', me, application: { values, error: error.message } };
        return { kind: 'signed-in', me, application: { values, error: SEND_FAILED, retry: true } };
      }
      return { kind: 'signed-in', me: { ...me, partner } };
    },

    async openLeads(me, pathname) {
      if (isReferPath(pathname)) return { kind: 'signed-in', me, leads: { kind: 'refer' } };
      if (isProfilePath(pathname)) return { kind: 'signed-in', me, leads: { kind: 'profile' } };
      const id = leadIdOf(pathname);
      try {
        if (id === null) return { kind: 'signed-in', me, leads: { kind: 'list', referrals: await fetchReferrals() } };
        return { kind: 'signed-in', me, leads: { kind: 'lead', referral: await fetchReferral(id) } };
      } catch (error) {
        const failed: Screen = { kind: 'signed-in', me, leads: { kind: 'failed', path: pathname } };
        if (!(error instanceof PartnerApiError)) return failed;
        if (id !== null && error.status === 404) return { kind: 'signed-in', me, leads: { kind: 'no-such-lead' } };
        if (error.status === 401) {
          await dropRefusedSession();
          return { kind: 'sign-in', notice: error.message };
        }
        if (error.status !== 403) return failed;
        return afterForbidden((verified) => ({ ...failed, me: verified }));
      }
    },

    async refer(me, values) {
      const form = (state: Omit<ReferralFormState, 'values'>, who: PartnerMe = me): Screen => ({
        kind: 'signed-in',
        me: who,
        leads: { kind: 'refer', form: { values, ...state } },
      });
      const fieldErrors = validateReferral(values);
      if (Object.keys(fieldErrors).length > 0) return form({ fieldErrors });
      let referral: Referral;
      try {
        referral = await refer(referralBody(values));
      } catch (error) {
        if (!(error instanceof PartnerApiError)) return form({ error: REFER_FAILED, retry: true });
        if (error.status === 401) {
          await dropRefusedSession();
          return { kind: 'sign-in', notice: error.message };
        }
        if (error.status === 400 && Object.keys(error.fields).length > 0) return form({ fieldErrors: error.fields });
        if (error.status === 400) return form({ error: error.message });
        if (error.status === 403) return afterForbidden((verified) => form({ error: REFER_FAILED, retry: true }, verified));
        return form({ error: REFER_FAILED, retry: true });
      }
      return { kind: 'signed-in', me, leads: { kind: 'lead', referral, notice: LEAD_SUBMITTED } };
    },

    async saveProfile(me, values) {
      const form = (state: Omit<ProfileFormState, 'values'>, who: PartnerMe = me): Screen => ({
        kind: 'signed-in',
        me: who,
        leads: { kind: 'profile', form: { values, ...state } },
      });
      const change = profileChange(profileValuesOf(me), values);
      if (Object.keys(change).length === 0) return form({});
      let saved: PartnerMe;
      try {
        saved = await updateProfile(change);
      } catch (error) {
        if (!(error instanceof PartnerApiError)) return form({ error: SAVE_FAILED, retry: true });
        if (error.status === 401) {
          await dropRefusedSession();
          return { kind: 'sign-in', notice: error.message };
        }
        if (error.status === 400) return form({ error: error.message, fieldErrors: error.fields });
        if (error.status === 403) return afterForbidden((verified) => form({ error: SAVE_FAILED, retry: true }, verified));
        return form({ error: error.message, retry: true });
      }
      return { kind: 'signed-in', me: saved, leads: { kind: 'profile', notice: PROFILE_SAVED } };
    },
  };
}
